import { get, startCase } from 'lodash';

export class DimensionProperty {
  constructor(
    readonly name: string,
    // eslint-disable-next-line @typescript-eslint/default-param-last
    readonly meta: any = null,
    // eslint-disable-next-line @typescript-eslint/default-param-last
    readonly isSearchable: boolean = false,
    readonly dimensionName: string,
  ) {}

  get displayName() {
    return get(this.meta, 'display.name') || startCase(this.name.replace(/_/g, ' '));
  }

  get isSortable() {
    return this.meta?.order?.enabled;
  }

  public asJson() {
    return {
      name: this.name,
      meta: this.meta,
      is_searchable: this.isSearchable,
      dimension_name: this.dimensionName,
    };
  }
}

export class Dimension {
  constructor(
    readonly name: string,
    readonly meta: any = null,
    readonly properties: DimensionProperty[] = [],
    readonly isSearchable: boolean = false,
  ) {}

  get displayName(): string {
    return get(this.meta, 'display.name') || startCase(this.name.replace(/_/g, ' '));
  }

  get isRequired(): boolean {
    return get(this.meta, 'required') || false;
  }

  get isSortable(): boolean {
    return this.meta?.order?.enabled;
  }

  get hasProperties(): boolean {
    return !!this.properties;
  }

  public asJson() {
    return {
      name: this.name,
      meta: this.meta,
      properties: this.properties.map(property => property.asJson()),
      is_searchable: this.isSearchable,
    };
  }
}

export class Metric {
  constructor(readonly name: string, readonly meta: any = null) {}

  get displayName() {
    return get(this.meta, 'display.name') || startCase(this.name.replace(/_/g, ' '));
  }

  get format() {
    return get(this.meta, 'display.format');
  }

  get isSortable() {
    return get(this.meta, 'order.enabled');
  }
}

class MetricSet {
  constructor(readonly name: string, readonly metrics: Metric[]) {}
}

export class Rollup {
  readonly name;
  constructor(readonly value: string) {
    this.name = startCase(value);
  }

  equals(rollup: Rollup) {
    return this.value === rollup.value;
  }
}

class Cube {
  private _dimensions = new Map<string, Dimension>();
  private _metrics = new Map<string, Metric>();

  constructor(dimensions: Dimension[], metrics: Metric[], public readonly rollups: Rollup[]) {
    for (const dimension of dimensions) {
      this._dimensions.set(dimension.name, dimension);
    }
    for (const metric of metrics) {
      this._metrics.set(metric.name, metric);
    }
  }

  get name() {
    return [...this.dimensions.map(d => d.name)].sort().join(',');
  }

  get dimensions(): Dimension[] {
    return Array.from(this._dimensions.values());
  }

  get metrics(): Metric[] {
    return Array.from(this._metrics.values());
  }

  hasDimension(dimensionName) {
    return this._dimensions.has(dimensionName);
  }

  hasDimensions(dimensionNames) {
    return dimensionNames.every(name => this.hasDimension(name));
  }
}

class Fact {
  private _dimensions = new Map<string, Dimension>();
  private _metrics = new Map<string, Metric>();

  constructor(public readonly name: string, dimensions: Dimension[], metrics: Metric[], public readonly rollups: Rollup[]) {
    for (const dimension of dimensions) {
      this._dimensions.set(dimension.name, dimension);
    }
    for (const metric of metrics) {
      this._metrics.set(metric.name, metric);
    }
  }

  get dimensions(): Dimension[] {
    return Array.from(this._dimensions.values());
  }

  get metrics(): Metric[] {
    return Array.from(this._metrics.values());
  }

  hasMetric(metricName) {
    return this._metrics.has(metricName);
  }

  hasMetrics(metricNames) {
    return metricNames.every(name => this.hasMetric(name));
  }

  hasDimension(dimensionName) {
    return this._dimensions.has(dimensionName) || dimensionName === 'yearmonth' || dimensionName === 'date';
  }

  hasDimensions(dimensionNames) {
    return dimensionNames.every(name => this.hasDimension(name));
  }
}

export class Schema {
  private _meta: any;
  private _dimensions = new Map<string, Dimension>();
  private _metricSets = new Map<string, MetricSet>();
  private _metrics = new Map<string, Metric>();
  private _cubes = new Map<string, Cube>();
  private _facts = new Map<string, Fact>();
  private _rollups = new Map<string, Rollup>();

  constructor(rawSchema) {
    this._meta = rawSchema['meta'];
    this.parseSchema(rawSchema);
  }

  get displayName(): string {
    return get(this._meta, 'display.name');
  }

  get dimensions(): Dimension[] {
    return [...Array.from(this._dimensions.values())].sort((a, b) => a.displayName.localeCompare(b.displayName));
  }

  get metrics(): Metric[] {
    return [...Array.from(this._metrics.values())].sort((a, b) => a.displayName.localeCompare(b.displayName));
  }

  get rollups(): Rollup[] {
    return Array.from(this._rollups.values());
  }

  fetchDimension(dimensionName): Dimension {
    return this._dimensions.get(dimensionName);
  }

  fetchMetric(metricName): Metric {
    return this._metrics.get(metricName);
  }

  isCubeBased() {
    return this._cubes.size > 0;
  }

  isFactBased() {
    return this._facts.size > 0;
  }

  hasCube(dimensions: Dimension[]) {
    const dimensionNames = dimensions.map(d => d.name);
    if (dimensions.length !== [...new Set(dimensionNames)].length) {
      return false;
    }

    if (this.isCubeBased()) {
      const cube = [...new Set(['entity', ...dimensionNames].sort())].join(',');
      return this._cubes.has(cube);
    } else {
      return this.compatibleFacts(dimensionNames).length > 0;
    }
  }

  requiredDimensions(): Dimension[] {
    return this.dimensions.filter(dimension => dimension.isRequired);
  }

  compatibleDimensions(source: (Dimension | DimensionProperty)[], metrics: Metric[] = []): Dimension[] {
    const dimensionNames = source.map(dim => {
      if (dim instanceof Dimension) {
        return dim.name;
      } else {
        return dim?.dimensionName;
      }
    });
    const metricNames = metrics.map(metric => metric.name);

    const compatibleDimensions = new Map<string, Dimension>();
    if (this.isCubeBased()) {
      for (const cube of this.compatibleCubes(dimensionNames)) {
        for (const dimension of cube.dimensions) {
          compatibleDimensions.set(dimension.name, dimension);
        }
      }
    } else {
      for (const fact of this.compatibleFacts(dimensionNames, metricNames)) {
        for (const dimension of fact.dimensions) {
          compatibleDimensions.set(dimension.name, dimension);
        }
      }
    }
    return Array.from(compatibleDimensions.values());
  }

  compatibleMetrics(dimensions: Dimension[], metrics: Metric[]): Metric[] {
    const dimensionNames = dimensions.map(d => d.name);
    const metricNames = metrics.map(m => m.name);
    if (this.isCubeBased()) {
      const name = [...new Set(['entity', ...dimensionNames].sort())].join(',');
      const cube = this._cubes.get(name);
      return cube ? cube.metrics : [];
    } else {
      const compatibleMetrics = new Map<string, Metric>();
      for (const fact of this.compatibleFacts(dimensionNames, metricNames)) {
        for (const metric of fact.metrics) {
          compatibleMetrics.set(metric.name, metric);
        }
      }
      return Array.from(compatibleMetrics.values());
    }
  }

  compatibleRollups(dimensions: Dimension[]): Rollup[] {
    const dimensionNames = dimensions.map(d => d.name);
    const name = [...new Set(['entity', ...dimensionNames].sort())].join(',');
    let rollups;
    if (this.isCubeBased()) {
      const cube = this._cubes.get(name);
      rollups = cube.rollups;
    } else {
      const fact = this._facts.get(name);
      rollups = fact.rollups;
    }
    return rollups;
  }

  private compatibleCubes(dimensionNames: string[]): Cube[] {
    return Array.from(this._cubes.values())
      .filter(cube => cube.hasDimensions(dimensionNames));
  }

  private compatibleFacts(dimensionNames: string[], metricNames: string[] = []): Fact[] {
    return Array.from(this._facts.values())
      .filter(fact => fact.hasDimensions(dimensionNames) && fact.hasMetrics(metricNames));
  }

  private parseSchema(rawSchema) {
    for (const rawDimension of rawSchema['dimensions']) {
      const dimension = this.parseDimension(rawDimension);
      this._dimensions.set(dimension.name, dimension);
    }

    for (const rawMetricSet of rawSchema['metric_sets']) {
      const metricSet = this.parseMetricSet(rawMetricSet);
      this._metricSets.set(metricSet.name, metricSet);
      for (const metric of metricSet.metrics) {
        this._metrics.set(metric.name, metric);
      }
    }

    for (const rawCube of (rawSchema['cubes'] || [])) {
      const cube = this.parseCube(rawCube);
      this._cubes.set(cube.name, cube);

      for (const rollup of cube.rollups) {
        this._rollups.set(rollup.name, rollup);
      }
    }

    for (const rawFact of (rawSchema['facts'] || [])) {
      const fact = this.parseFact(rawFact);
      this._facts.set(fact.name, fact);

      for (const rollup of fact.rollups) {
        this._rollups.set(rollup.name, rollup);
      }
    }
  }

  private parseDimension(rawDimension) {
    const dimensionName = rawDimension['name'];
    const properties = (rawDimension['properties'] || []).map(rp => new DimensionProperty(rp['property'], rp['meta'], !!rp.meta?.search, dimensionName));
    const filteredProperties = properties.filter(dp => dp.name !== dimensionName);
    const isSearchable = !!properties.find(dp => get(dp, 'meta.search'));
    return new Dimension(dimensionName, rawDimension['meta'], filteredProperties, isSearchable);
  }

  private parseMetricSet(rawMetricSet) {
    const metrics = rawMetricSet['metrics'].map(rawMetric => {
      return new Metric(rawMetric['name'], rawMetric['meta']);
    });

    return new MetricSet(rawMetricSet['name'], metrics);
  }

  private parseCube(rawCube) {
    const dimensions = rawCube['dimensions'].map(name => this._dimensions.get(name));
    const metrics = [
      ...new Set(
        rawCube['metric_sets']
          .map(name => this._metricSets.get(name))
          .filter(Boolean)
          .map(ms => ms.metrics)
          .flat(),
      ),
    ] as Metric[];
    const rollups = rawCube['rollups'].map(value => new Rollup(value));

    return new Cube(dimensions, metrics, rollups);
  }

  private parseFact(rawFact) {
    const dimensions = rawFact['dimensions'].map(name => this._dimensions.get(name));
    const metrics = [
      ...new Set(
        rawFact['metric_sets']
          .map(name => this._metricSets.get(name))
          .filter(Boolean)
          .map(ms => ms.metrics)
          .flat(),
      ),
    ] as Metric[];
    const rollups = rawFact['rollups'].map(value => new Rollup(value));

    return new Fact(rawFact['name'], dimensions, metrics, rollups);
  }
}
